<template>
  <div class="exam-page">
    <header class="exam-header">
      <van-icon name="arrow-left" class="back-icon" @click="goBack" />
      <span>体检预约</span>
    </header>

    <div v-if="loading && !detail" class="state-cell">
      <van-loading color="#168de0" size="22" vertical>加载中...</van-loading>
    </div>

    <template v-if="detail">
      <section class="exam-card">
        <div class="card-title">
          {{ detail.nickname || "客户信息" }}
          <span class="exam-badge" :class="`exam-${examStatus}`">{{ examStatusText }}</span>
        </div>
        <div class="info-row"><span>手机号</span><b>{{ detail.phone || "-" }}</b></div>
        <div class="info-row"><span>身份证</span><b>{{ detail.idCard || "-" }}</b></div>
        <div v-if="detail.cardNumber" class="info-row">
          <span>体检卡号</span><b>{{ detail.cardNumber }}</b>
        </div>
        <template v-if="examStatus === 1 || examStatus === 3 || examStatus === 4 || examStatus === 5">
          <div class="info-row">
            <span>体检日期</span><b>{{ formatDate(detail.reservationDate) }}
              <template v-if="detail.reservationTime"> {{ detail.reservationTime }}</template>
            </b>
          </div>
          <div class="info-row"><span>体检套餐</span><b>{{ detail.packageName || "-" }}</b></div>
          <div class="info-row"><span>体检机构</span><b>{{ detail.unitsName || "-" }}</b></div>
          <div v-if="detail.unitsAddress" class="info-row"><span>机构地址</span><b>{{ detail.unitsAddress }}</b></div>
          <div v-if="detail.checkupDate" class="info-row"><span>实际到检</span><b>{{ detail.checkupDate }}</b></div>
          <div v-if="examStatus === 4 || examStatus === 5" class="info-row"><span>报告状态</span><b>{{ examStatusText }}</b></div>
        </template>
        <div v-if="examStatus === 2" class="status-tip">
          预约已取消，体检权益已恢复，可重新预约。
        </div>
        <div v-if="examStatus === 0 && !detail.cardNumber" class="status-tip">
          暂未预约，请选择套餐与机构完成预约。
        </div>
        <div v-if="examStatus === 3" class="status-tip">已到检，报告出具后自动更新</div>
        <div v-if="examStatus === 4" class="status-tip">体检已完成，报告已出</div>
        <div v-if="examStatus === 5" class="status-tip">体检报告已上传</div>
      </section>

      <section v-if="examStatus === 1" class="exam-notice">
        <div class="notice-title"><van-icon name="info-o" />体检温馨提示</div>
        <p>体检前一天请尽量饮食清淡，晚 20:00 后请勿进食，可少量饮水。</p>
        <p>体检当天请携带身份证原件，空腹前往体检中心前台登记体检。</p>
      </section>

      <div class="exam-footer">
        <template v-if="examStatus === 0 || examStatus === 2">
          <button
            type="button"
            class="action-button primary"
            :disabled="submitting"
            @click="openBook"
          >
            {{ examStatus === 2 ? "重新预约" : "预约体检" }}
          </button>
        </template>
        <template v-else-if="examStatus === 1">
          <div v-if="lockedToday" class="lock-tip">体检当日不可改期或取消</div>
          <div v-else class="action-row">
            <button type="button" class="action-button ghost" :disabled="submitting" @click="openChange">
              改期
            </button>
            <button type="button" class="action-button danger" :disabled="submitting" @click="confirmCancel">
              取消预约
            </button>
          </div>
        </template>
      </div>
    </template>

    <!-- 预约 / 改期 弹层 -->
    <van-popup
      v-model="showPopup"
      position="bottom"
      round
      :style="{ maxHeight: '92vh', height: '88vh' }"
      class="book-popup"
    >
      <div class="popup-header">
        <span>{{ mode === 'book' ? '预约体检' : '改期' }}</span>
        <van-icon name="cross" @click="showPopup = false" />
      </div>

      <div class="popup-body" :class="{ 'city-picker-body': mode === 'book' && step === 2 }">
        <!-- 步骤 1 套餐 -->
        <template v-if="mode === 'book' && step === 1">
          <div class="step-label">选择套餐（已按客户病种及性别匹配）</div>
          <div v-if="queryLoading" class="state-cell small"><van-loading color="#168de0" size="20" vertical>查询中...</van-loading></div>
          <div v-else-if="!packages.length" class="empty-tip">{{ packageTip || '暂无匹配套餐，请先完善客户身份证和当前有效问卷' }}</div>
          <div
            v-for="pkg in packages"
            :key="pkg.package_id"
            class="option-card"
            :class="{ selected: selectedPkg && selectedPkg.package_id === pkg.package_id }"
            @click="choosePackage(pkg)"
          >
            <b>{{ pkg.package_name || pkg.package_shortname }}</b>
            <small v-if="pkg.package_sex">{{ pkg.package_sex === '1' ? '男' : '女' }}士适用</small>
            <small>{{ pkg.region_name }}地区套餐</small>
          </div>
        </template>

        <!-- 步骤 2 城市 -->
        <div v-if="mode === 'book' && step === 2" class="city-picker">
          <div class="step-label">
            选择城市
            <small class="step-back" @click="step = 1">返回套餐</small>
          </div>
          <div class="city-search">
            <van-icon name="search" />
            <input
              v-model="cityKeyword"
              type="search"
              placeholder="输入城市名称或首字母，如广州、G"
              aria-label="搜索可预约城市"
              @keyup.enter="$event.target.blur()"
            />
            <button v-if="cityKeyword" type="button" class="city-search-clear" aria-label="清空搜索" @click="cityKeyword = ''">
              <van-icon name="clear" />
            </button>
          </div>
          <div v-if="queryLoading" class="state-cell small"><van-loading color="#168de0" size="20" vertical>查询中...</van-loading></div>
          <div v-else-if="cityTip" class="empty-tip">
            {{ cityTip }}
            <button type="button" class="city-retry" @click="loadCities">重新加载</button>
          </div>
          <template v-else>
            <div class="city-count">
              <span>{{ cityKeyword.trim() ? '匹配到' : '共' }} {{ matchedCityCount }} 个可预约城市</span>
              <span v-if="cityGroups.length > 1" class="city-index-help">右侧字母可按住滑动</span>
            </div>
            <div v-if="!cityGroups.length" class="empty-tip">
              {{ cityKeyword.trim() ? '未找到匹配的可预约城市，请尝试其他名称' : '当前套餐暂无可预约城市，请返回选择其他套餐' }}
            </div>
            <div v-else class="city-results">
              <div ref="cityScroll" class="city-scroll" :class="{ 'with-index': cityGroups.length > 1 }">
                <section v-for="group in cityGroups" :key="group.letter" ref="cityGroups" :data-letter="group.letter" class="city-group">
                  <h3 class="city-group-title">{{ group.letter === '#' ? '其他城市' : group.letter }}</h3>
                  <div class="city-grid">
                    <button
                      v-for="city in group.cities"
                      :key="city.city_id"
                      type="button"
                      class="city-item"
                      :class="{ selected: selectedCity && selectedCity.city_id === city.city_id }"
                      @click="chooseCity(city)"
                    >
                      {{ city.city_name }}
                    </button>
                  </div>
                </section>
              </div>
              <nav
                v-if="cityGroups.length > 1"
                ref="cityIndex"
                class="city-index"
                :class="{ dragging: cityIndexDragging }"
                :style="{ height: cityGroups.length * 26 + 'px' }"
                aria-label="城市首字母索引，可按住上下滑动"
                @touchstart.stop.prevent="startCityIndexTouch"
                @touchmove.stop.prevent="moveCityIndexTouch"
                @touchend.stop.prevent="endCityIndexTouch"
                @touchcancel.stop.prevent="finishCityIndexDrag"
                @pointerdown="startCityIndexPointer"
                @pointermove="moveCityIndexPointer"
                @pointerup="finishCityIndexPointer"
                @pointercancel="finishCityIndexPointer"
                @lostpointercapture="finishCityIndexPointer"
              >
                <button v-for="group in cityGroups" :key="group.letter" type="button" :class="{ active: cityIndexLetter === group.letter }" :aria-current="cityIndexLetter === group.letter ? 'true' : null" :aria-label="'跳转到 ' + group.letter + ' 分组'" @click="clickCityIndex(group.letter, $event)">
                  {{ group.letter }}
                </button>
              </nav>
              <div v-if="cityIndexDragging && cityIndexLetter" class="city-index-preview" :style="{ top: cityIndexPreviewTop + 'px' }" aria-hidden="true">
                {{ cityIndexLetter }}
              </div>
            </div>
          </template>
        </div>

        <!-- 步骤 3 机构 -->
        <template v-if="mode === 'book' && step === 3">
          <div class="step-label">
            选择体检机构
            <small class="step-back" @click="step = 2">返回城市</small>
          </div>
          <div v-if="queryLoading" class="state-cell small"><van-loading color="#168de0" size="20" vertical>查询中...</van-loading></div>
          <div
            v-for="unit in units"
            :key="unit.units_id"
            class="option-card"
            :class="{ selected: selectedUnit && selectedUnit.units_id === unit.units_id }"
            @click="chooseUnit(unit)"
          >
            <b>{{ unit.units_name }}</b>
            <small>{{ unit.units_address }}</small>
          </div>
        </template>

        <!-- 步骤 4 日期/时段 -->
        <div v-if="(mode === 'book' && step === 4) || mode === 'change'">
          <div v-if="mode === 'book'" class="step-label">
            选择日期与时段
            <small class="step-back" @click="step = 3">返回机构</small>
          </div>
          <div v-else class="step-label">选择新的日期与时段</div>

          <div class="month-nav">
            <van-icon name="arrow-left" :class="{ disabled: !canPrevMonth }" @click="shiftMonth(-1)" />
            <b>{{ month }}</b>
            <van-icon name="arrow" :class="{ disabled: !canNextMonth }" @click="shiftMonth(1)" />
          </div>

          <div v-if="queryLoading" class="state-cell small"><van-loading color="#168de0" size="20" vertical>排期查询中...</van-loading></div>
          <template v-else>
            <div v-if="!schedules.length" class="empty-tip">该月暂无可约排期</div>
            <div class="calendar">
              <div class="calendar-weekdays">
                <span>日</span><span>一</span><span>二</span><span>三</span><span>四</span><span>五</span><span>六</span>
              </div>
              <div class="calendar-grid">
                <div
                  v-for="(day, idx) in calendarDays"
                  :key="idx"
                  class="calendar-cell"
                  :class="{
                    empty: day.empty,
                    selected: !day.empty && selectedDate === day.clickday,
                    license: !day.empty && day.type === 'license',
                    full: !day.empty && day.type === 'full',
                    disabled: !day.empty && day.type !== 'license'
                  }"
                  @click="!day.empty && chooseDate(day)"
                >
                  <template v-if="!day.empty">
                    <b>{{ day.day }}</b>
                    <small>{{ day.type === 'license' ? '可约' : day.type === 'full' ? '约满' : '禁约' }}</small>
                  </template>
                </div>
              </div>
            </div>

            <div v-if="timeSlots.length" class="time-row">
              <span class="time-label">时段</span>
              <span
                v-for="slot in timeSlots"
                :key="slot"
                class="time-item"
                :class="{ selected: selectedTime === slot }"
                @click="selectedTime = slot"
              >
                {{ slot }}
              </span>
            </div>

          </template>
        </div>
      </div>

      <!-- 提交按钮固定在弹层底部 -->
      <div
        v-if="(mode === 'book' && step === 4) || mode === 'change'"
        class="popup-footer"
      >
        <button
          type="button"
          class="action-button primary submit-btn"
          :disabled="submitting || queryLoading || !selectedDate || !selectedTime"
          @click="submit"
        >
          {{ submitting ? '提交中...' : mode === 'book' ? '提交预约' : '确认改期' }}
        </button>
      </div>
    </van-popup>

    <van-dialog
      v-model="showCancelConfirm"
      title="取消预约"
      show-cancel-button
      :confirm-button-color="'#b14545'"
      @confirm="doCancel"
    >
      <div class="dialog-body">
        确认取消「{{ detail && (detail.nickname || detail.phone) }}」的体检预约？<br />
        取消后体检权益恢复，可重新预约。
      </div>
    </van-dialog>

    <van-overlay :show="submitting">
      <van-loading class="submit-loading" size="40" vertical>提交中...</van-loading>
    </van-overlay>
  </div>
</template>

<script>
import { Toast } from "vant";
import {
  getSanGaoExamDetail,
  getSanGaoExamPackages,
  getSanGaoExamCities,
  getSanGaoExamUnits,
  getSanGaoExamSchedules,
  bookSanGaoExam,
  changeSanGaoExamDate,
  cancelSanGaoExam,
} from "@/api/sanGao";

export default {
  name: "SanGaoSalesmanExam",
  data() {
    return {
      memberId: null,
      detail: null,
      loading: false,
      submitting: false,
      // 预约/改期弹层
      showPopup: false,
      mode: "book", // book | change
      step: 1,
      queryLoading: false,
      querySequence: 0,
      packageTip: "",
      packages: [],
      cities: [],
      cityKeyword: "",
      cityTip: "",
      cityIndexDragging: false,
      cityIndexLetter: "",
      cityIndexPreviewTop: 30,
      cityIndexTouchId: null,
      cityIndexPointerId: null,
      cityIndexClickBlockedUntil: 0,
      units: [],
      schedules: [],
      selectedPkg: null,
      selectedCity: null,
      selectedUnit: null,
      month: "",
      selectedDate: "",
      selectedTime: "",
      showCancelConfirm: false,
    };
  },
  computed: {
    cityGroups() {
      const keyword = this.cityKeyword.trim().replace(/\s+/g, "").toLowerCase();
      const groups = {};
      this.cities.forEach((city) => {
        if (!city || city.city_id == null || !city.city_name) return;
        const name = String(city.city_name).replace(/\s+/g, "").toLowerCase();
        const initial = String(city.city_initial || "").trim().charAt(0).toUpperCase();
        const letter = /^[A-Z]$/.test(initial) ? initial : "#";
        if (keyword && !name.includes(keyword) && letter.toLowerCase() !== keyword) return;
        if (!groups[letter]) groups[letter] = [];
        groups[letter].push(city);
      });
      return Object.keys(groups)
        .sort((a, b) => a === b ? 0 : a === "#" ? 1 : b === "#" ? -1 : a.localeCompare(b))
        .map((letter) => ({
          letter,
          cities: groups[letter].slice().sort((a, b) => String(a.city_name).localeCompare(String(b.city_name), "zh-CN")),
        }));
    },
    matchedCityCount() {
      return this.cityGroups.reduce((total, group) => total + group.cities.length, 0);
    },
    examStatus() {
      const status = this.detail && this.detail.examStatus;
      return status === null || status === undefined ? 0 : status;
    },
    examStatusText() {
      if (this.examStatus === 0) return "未预约";
      if (this.examStatus === 1) return "已预约";
      if (this.examStatus === 2) return "已取消";
      if (this.examStatus === 3) return "已到检";
      if (this.examStatus === 4) return "已出报告";
      if (this.examStatus === 5) return "已传报告";
      return "-";
    },
    lockedToday() {
      if (!this.detail || !this.detail.reservationDate) return false;
      const now = new Date();
      const pad = (n) => (n < 10 ? `0${n}` : String(n));
      const today = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
      return today === this.detail.reservationDate;
    },
    timeSlots() {
      const day = this.schedules.find((s) => s.clickday === this.selectedDate);
      if (!day || !Array.isArray(day.period)) return [];
      return day.period;
    },
    canPrevMonth() {
      const now = new Date();
      const pad = (n) => (n < 10 ? `0${n}` : String(n));
      const current = `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
      return this.month > current;
    },
    canNextMonth() {
      const limit = new Date();
      limit.setMonth(limit.getMonth() + 3);
      const pad = (n) => (n < 10 ? `0${n}` : String(n));
      const limitStr = `${limit.getFullYear()}-${pad(limit.getMonth() + 1)}`;
      return this.month < limitStr;
    },
    calendarDays() {
      if (!this.month) return [];
      const [year, month] = this.month.split("-").map(Number);
      const firstWeekday = new Date(year, month - 1, 1).getDay(); // 0=周日
      const daysInMonth = new Date(year, month, 0).getDate();
      const scheduleMap = {};
      this.schedules.forEach((s) => {
        if (s && s.clickday) scheduleMap[s.clickday] = s;
      });
      const pad = (n) => (n < 10 ? `0${n}` : String(n));
      const today = (() => {
        const d = new Date();
        return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
      })();
      const days = [];
      for (let i = 0; i < firstWeekday; i++) days.push({ empty: true });
      for (let d = 1; d <= daysInMonth; d++) {
        const clickday = `${year}${pad(month)}${pad(d)}`;
        const schedule = scheduleMap[clickday];
        const type = schedule ? schedule.type : "forbid";
        const past = clickday < today;
        days.push({
          day: d,
          clickday,
          type,
          past,
          schedule,
        });
      }
      // 尾部补齐到 7 的倍数，保持网格整齐
      while (days.length % 7 !== 0) days.push({ empty: true });
      return days;
    },
  },
  watch: {
    showPopup(value) {
      if (!value) this.finishCityIndexDrag();
    },
    step(value) {
      if (value !== 2) this.finishCityIndexDrag();
    },
    cityGroups() {
      this.finishCityIndexDrag();
      this.cityIndexLetter = "";
    },
    cityKeyword() {
      this.$nextTick(() => {
        if (this.$refs.cityScroll) this.$refs.cityScroll.scrollTop = 0;
      });
    },
  },
  beforeDestroy() {
    this.finishCityIndexDrag();
  },
  created() {
    this.memberId = this.$route.query.memberId;
    if (!this.memberId) {
      Toast("参数缺失");
      this.goBack();
      return;
    }
    this.loadDetail();
  },
  methods: {
    async loadDetail() {
      this.loading = true;
      try {
        const res = await getSanGaoExamDetail(this.memberId);
        if (res && res.code === 200 && res.data) {
          this.detail = res.data;
        } else {
          Toast((res && res.msg) || "加载体检信息失败");
        }
      } catch (e) {
        Toast("网络异常，请稍后重试");
      } finally {
        this.loading = false;
      }
    },
    // ==================== 预约流程 ====================
    openBook() {
      this.mode = "book";
      this.step = 1;
      this.resetSelection();
      this.showPopup = true;
      this.loadPackages();
    },
    openChange() {
      this.mode = "change";
      this.resetSelection();
      this.month = this.currentMonth();
      this.showPopup = true;
      this.loadSchedules();
    },
    resetSelection() {
      this.querySequence += 1;
      this.packages = [];
      this.cities = [];
      this.cityKeyword = "";
      this.cityTip = "";
      this.units = [];
      this.packageTip = "";
      this.selectedPkg = null;
      this.selectedCity = null;
      this.selectedUnit = null;
      this.selectedDate = "";
      this.selectedTime = "";
      this.month = this.currentMonth();
      this.schedules = [];
    },
    currentMonth() {
      const now = new Date();
      const pad = (n) => (n < 10 ? `0${n}` : String(n));
      return `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
    },
    async loadPackages() {
      const sequence = ++this.querySequence;
      this.packages = [];
      this.packageTip = "";
      this.queryLoading = true;
      try {
        const res = await getSanGaoExamPackages(this.memberId);
        if (sequence !== this.querySequence) return;
        if (res && res.code === 200 && Array.isArray(res.data)) {
          this.packages = res.data;
        } else {
          this.packageTip = (res && res.msg) || "套餐查询失败，请重新打开预约重试";
          Toast(this.packageTip);
        }
      } catch (e) {
        if (sequence !== this.querySequence) return;
        this.packageTip = "网络异常，请重新打开预约重试";
        Toast(this.packageTip);
      } finally {
        if (sequence === this.querySequence) this.queryLoading = false;
      }
    },
    choosePackage(pkg) {
      this.selectedPkg = pkg;
      this.selectedCity = null;
      this.selectedUnit = null;
      this.cities = [];
      this.cityKeyword = "";
      this.cityTip = "";
      this.units = [];
      this.schedules = [];
      this.selectedDate = "";
      this.selectedTime = "";
      this.step = 2;
      this.loadCities();
    },
    async loadCities() {
      const sequence = ++this.querySequence;
      this.cities = [];
      this.cityTip = "";
      this.queryLoading = true;
      try {
        const res = await getSanGaoExamCities(this.selectedPkg.package_id);
        if (sequence !== this.querySequence) return;
        if (res && res.code === 200 && Array.isArray(res.data)) {
          this.cities = res.data;
        } else {
          this.cityTip = (res && res.msg) || "城市查询失败，请重试";
          Toast(this.cityTip);
        }
      } catch (e) {
        if (sequence !== this.querySequence) return;
        this.cityTip = "网络异常，请稍后重试";
        Toast(this.cityTip);
      } finally {
        if (sequence === this.querySequence) this.queryLoading = false;
      }
    },
    scrollToCityGroup(letter) {
      const container = this.$refs.cityScroll;
      const sections = this.$refs.cityGroups || [];
      const section = sections.find((element) => element.dataset.letter === letter);
      if (container && section) {
        this.cityIndexLetter = letter;
        container.scrollTop += section.getBoundingClientRect().top - container.getBoundingClientRect().top;
      }
    },
    clickCityIndex(letter, event) {
      // 拖动结束后的兼容 click 可能落回起点；键盘触发的 click(detail=0)仍可正常使用。
      if (event && event.detail !== 0 && Date.now() < this.cityIndexClickBlockedUntil) return;
      this.scrollToCityGroup(letter);
    },
    locateCityIndex(clientY) {
      const index = this.$refs.cityIndex;
      if (!index || !this.cityGroups.length) return;
      const rect = index.getBoundingClientRect();
      if (rect.height <= 0) return;
      // 按整条触摸区域等分定位，手指轻微横向偏移仍可继续滑动；上下越界夹到首尾。
      const position = Math.max(0, Math.min(this.cityGroups.length - 1,
        Math.floor((clientY - rect.top) / rect.height * this.cityGroups.length)));
      const letter = this.cityGroups[position].letter;
      this.cityIndexPreviewTop = Math.max(30, Math.min(rect.height - 30, clientY - rect.top));
      if (letter !== this.cityIndexLetter) this.scrollToCityGroup(letter);
    },
    startCityIndexTouch(event) {
      if (event.touches.length !== 1 || this.cityIndexDragging) return;
      const touch = event.touches[0];
      this.cityIndexTouchId = touch.identifier;
      this.cityIndexDragging = true;
      this.cityIndexLetter = "";
      this.locateCityIndex(touch.clientY);
    },
    moveCityIndexTouch(event) {
      if (!this.cityIndexDragging || this.cityIndexTouchId === null) return;
      const touch = Array.from(event.touches).find((item) => item.identifier === this.cityIndexTouchId);
      if (touch) this.locateCityIndex(touch.clientY);
    },
    endCityIndexTouch(event) {
      const ended = Array.from(event.changedTouches).some((item) => item.identifier === this.cityIndexTouchId);
      if (ended) this.finishCityIndexDrag();
    },
    startCityIndexPointer(event) {
      // 手机用 Touch Events 兼容旧版微信；Pointer Events 仅用于鼠标/触控笔，避免双重触发。
      if (event.pointerType === "touch" || event.button !== 0 || this.cityIndexDragging) return;
      if (event.cancelable) event.preventDefault();
      const index = this.$refs.cityIndex;
      if (!index || !index.setPointerCapture) return;
      try {
        index.setPointerCapture(event.pointerId);
      } catch (e) {
        return; // 不支持捕获时仍保留普通点击跳转。
      }
      this.cityIndexPointerId = event.pointerId;
      this.cityIndexDragging = true;
      this.cityIndexLetter = "";
      this.locateCityIndex(event.clientY);
    },
    moveCityIndexPointer(event) {
      if (this.cityIndexDragging && event.pointerId === this.cityIndexPointerId) {
        this.locateCityIndex(event.clientY);
      }
    },
    finishCityIndexPointer(event) {
      if (event.pointerId === this.cityIndexPointerId) this.finishCityIndexDrag();
    },
    finishCityIndexDrag() {
      const index = this.$refs.cityIndex;
      const pointerId = this.cityIndexPointerId;
      if (this.cityIndexDragging) this.cityIndexClickBlockedUntil = Date.now() + 400;
      this.cityIndexDragging = false;
      this.cityIndexTouchId = null;
      this.cityIndexPointerId = null;
      if (index && pointerId !== null && index.hasPointerCapture && index.hasPointerCapture(pointerId)) {
        index.releasePointerCapture(pointerId);
      }
    },
    chooseCity(city) {
      this.selectedCity = city;
      this.selectedUnit = null;
      this.units = [];
      this.step = 3;
      this.loadUnits();
    },
    async loadUnits() {
      const sequence = ++this.querySequence;
      this.queryLoading = true;
      try {
        const res = await getSanGaoExamUnits({
          packageId: this.selectedPkg.package_id,
          cityId: this.selectedCity.city_id,
        });
        if (sequence !== this.querySequence) return;
        if (res && res.code === 200 && Array.isArray(res.data)) {
          this.units = res.data;
          if (!this.units.length) {
            Toast("该城市暂无可约机构");
          }
        } else {
          Toast((res && res.msg) || "机构查询失败");
        }
      } catch (e) {
        if (sequence !== this.querySequence) return;
        Toast("网络异常，请稍后重试");
      } finally {
        if (sequence === this.querySequence) this.queryLoading = false;
      }
    },
    chooseUnit(unit) {
      this.selectedUnit = unit;
      this.step = 4;
      this.selectedDate = "";
      this.selectedTime = "";
      this.loadSchedules();
    },
    shiftMonth(offset) {
      if (offset < 0 && !this.canPrevMonth) return;
      if (offset > 0 && !this.canNextMonth) return;
      const [year, monthNum] = this.month.split("-").map(Number);
      const date = new Date(year, monthNum - 1 + offset, 1);
      const pad = (n) => (n < 10 ? `0${n}` : String(n));
      this.month = `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;
      this.selectedDate = "";
      this.selectedTime = "";
      this.loadSchedules();
    },
    async loadSchedules() {
      const unitsId = this.mode === "book"
        ? this.selectedUnit && this.selectedUnit.units_id
        : this.detail && this.detail.unitsId;
      const packageId = this.mode === "book"
        ? this.selectedPkg && this.selectedPkg.package_id
        : this.detail && this.detail.packageId;
      if (!unitsId || !packageId) return;
      const sequence = ++this.querySequence;
      this.schedules = [];
      this.queryLoading = true;
      try {
        const res = await getSanGaoExamSchedules({
          unitsId,
          packageId,
          month: this.month,
        });
        if (sequence !== this.querySequence) return;
        if (res && res.code === 200 && Array.isArray(res.data)) {
          this.schedules = res.data;
        } else {
          this.schedules = [];
          Toast((res && res.msg) || "排期查询失败");
        }
      } catch (e) {
        if (sequence !== this.querySequence) return;
        Toast("网络异常，请稍后重试");
      } finally {
        if (sequence === this.querySequence) this.queryLoading = false;
      }
    },
    chooseDate(day) {
      if (day.empty || day.type !== "license" || day.past) return;
      this.selectedDate = day.clickday;
      this.selectedTime = "";
    },
    async submit() {
      if (this.submitting) return;
      this.submitting = true;
      try {
        let res;
        if (this.mode === "book") {
          res = await bookSanGaoExam({
            memberId: this.memberId,
            packageId: this.selectedPkg.package_id,
            packageName: this.selectedPkg.package_name || this.selectedPkg.package_shortname,
            cityId: this.selectedCity.city_id,
            cityName: this.selectedCity.city_name,
            unitsId: this.selectedUnit.units_id,
            unitsName: this.selectedUnit.units_name,
            unitsAddress: this.selectedUnit.units_address,
            reservationDate: this.selectedDate,
            reservationTime: this.selectedTime,
          });
        } else {
          res = await changeSanGaoExamDate({
            memberId: this.memberId,
            reservationDate: this.selectedDate,
            reservationTime: this.selectedTime,
          });
        }
        if (res && res.code === 200) {
          Toast(this.mode === "book" ? "预约成功" : "改期成功");
          this.showPopup = false;
          this.loadDetail();
        } else {
          Toast((res && res.msg) || "提交失败");
        }
      } catch (e) {
        Toast("网络异常，请稍后重试");
      } finally {
        this.submitting = false;
      }
    },
    // ==================== 取消 ====================
    confirmCancel() {
      this.showCancelConfirm = true;
    },
    async doCancel() {
      if (this.submitting) return;
      this.submitting = true;
      try {
        const res = await cancelSanGaoExam(this.memberId);
        if (res && res.code === 200) {
          Toast("已取消预约");
          this.loadDetail();
        } else {
          Toast((res && res.msg) || "取消失败");
        }
      } catch (e) {
        Toast("网络异常，请稍后重试");
      } finally {
        this.submitting = false;
      }
    },
    // ==================== 工具 ====================
    formatDate(value) {
      if (!value || value.length !== 8) return value || "-";
      return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
    },
    formatDay(clickday) {
      if (!clickday || clickday.length !== 8) return clickday;
      return `${clickday.slice(4, 6)}.${clickday.slice(6, 8)}`;
    },
    goBack() {
      this.$router.back();
    },
  },
};
</script>

<style lang="less" scoped>
.exam-page {
  min-height: 100vh;
  padding: 0 0 80px;
  background: linear-gradient(180deg, #0874ca 0%, #4ba8e0 18%, #dff6ff 40%, #f4fafd 100%);
}

.exam-header {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 0 14px;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  text-shadow: 0 2px 8px rgba(0, 48, 113, 0.35);
  background: linear-gradient(180deg, #0874ca, #2a94dd);

  .back-icon {
    position: absolute;
    left: 14px;
    font-size: 20px;
  }
}

.exam-card {
  margin: 14px 16px 0;
  padding: 14px 16px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 6px 16px rgba(12, 91, 147, 0.08);
}

.card-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #163b5c;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 5px 0;
  font-size: 13px;
  color: #5b7488;

  b {
    max-width: 68%;
    font-weight: 500;
    color: #163b5c;
    text-align: right;
    word-break: break-all;
  }
}

.exam-notice {
  margin: 12px 16px 0;
  padding: 12px 16px;
  border: 1px solid #d9ecfa;
  border-radius: 14px;
  background: #edf7ff;
  color: #416580;
  font-size: 13px;
  line-height: 1.7;

  .notice-title {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #1670c0;
    font-weight: 600;
  }

  p {
    margin: 6px 0 0;
  }
}

.exam-badge {
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;

  &.exam-0 { color: #7c8d9c; background: #eef4f9; }
  &.exam-1 { color: #1670c0; background: #ddf0fd; }
  &.exam-2 { color: #b6820b; background: #fff5d6; }
  &.exam-3 { color: #0f9c8f; background: #dffaf6; }
  &.exam-4 { color: #1b8a4b; background: #dff7e8; }
  &.exam-5 { color: #1b8a4b; background: #dff7e8; }
}

.status-tip,
.lock-tip {
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.6;
  color: #93a8b8;
}

.exam-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 14px 16px calc(20px + constant(safe-area-inset-bottom));
  padding-bottom: calc(20px + env(safe-area-inset-bottom));
  background: linear-gradient(180deg, rgba(255,255,255,0) 0%, #f4fafd 30%);

  .action-row {
    display: flex;
    gap: 12px;
  }
}

.action-button {
  flex: 1;
  height: 44px;
  border: 0;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 500;
  color: #fff;
  background: linear-gradient(100deg, #19a2e5, #146bd2);
  box-shadow: 0 4px 10px rgba(17, 112, 199, 0.25);
  cursor: pointer;
  text-align: center;
  line-height: 44px;

  &.primary:only-child {
    flex: 0 0 auto;
    width: 60%;
    margin: 0 auto;
    display: block;
  }

  &.ghost {
    color: #1670c0;
    background: #ddf0fd;
    box-shadow: none;
  }

  &.danger {
    color: #b14545;
    background: #fde0e0;
    box-shadow: none;
  }

  &:disabled {
    opacity: 0.6;
  }

  &.submit-btn {
    display: block;
    width: 100%;
  }
}

.state-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 0;
  color: #7c8d9c;

  &.small {
    padding: 18px 0;
  }
}

// ==================== 弹层 ====================
.book-popup {
  overflow: visible;
  display: flex;
  flex-direction: column;
}

.popup-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px 8px;
  font-size: 15px;
  font-weight: 600;
  color: #163b5c;
  background: #fff;
}

.popup-body {
  flex: 1;
  min-height: 0;
  padding: 0 18px 24px;
  overflow-y: auto;

  &.city-picker-body {
    display: flex;
    overflow: hidden;
    padding-bottom: 12px;
    padding-bottom: calc(12px + env(safe-area-inset-bottom));
  }
}

.popup-footer {
  flex-shrink: 0;
  padding: 12px 18px calc(14px + constant(safe-area-inset-bottom));
  padding-bottom: calc(14px + env(safe-area-inset-bottom));
  background: #fff;
}

.step-label {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin: 8px 0 10px;
  font-size: 13px;
  font-weight: 600;
  color: #163b5c;

  .step-back {
    font-size: 11px;
    font-weight: 400;
    color: #168de0;
    cursor: pointer;
  }
}

.option-card {
  padding: 12px 14px;
  margin-bottom: 8px;
  border: 1px solid #eef4f9;
  border-radius: 10px;
  background: #fafdff;
  cursor: pointer;

  b {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #163b5c;
  }

  small {
    display: block;
    margin-top: 4px;
    font-size: 11px;
    color: #7c8d9c;
    word-break: break-all;
  }

  &.selected {
    border-color: #168de0;
    background: #eaf6fe;
  }
}

.city-picker {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;

  > .step-label,
  > .city-search,
  > .city-count {
    flex-shrink: 0;
  }
}

.city-search {
  display: flex;
  align-items: center;
  padding: 0 10px;
  border: 1px solid #e2edf5;
  border-radius: 10px;
  background: #f4f8fc;
  color: #7c8d9c;

  input {
    flex: 1;
    min-width: 0;
    height: 40px;
    padding: 0 8px;
    border: 0;
    outline: none;
    background: transparent;
    color: #163b5c;
    font-size: 12px;
    -webkit-appearance: none;

    &::-webkit-search-cancel-button { -webkit-appearance: none; }
  }

  &:focus-within { border-color: #168de0; }
}

.city-search-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 36px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #7c8d9c;
}

.city-count {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 4px;
  padding: 10px 0 4px;
  font-size: 11px;
  color: #7c8d9c;
}

.city-index-help {
  color: #168de0;
}

.city-results {
  position: relative;
  flex: 1;
  min-height: 0;
}

.city-scroll {
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;

  // 为字母索引单独留出空间，避免滚动条覆盖字母按钮。
  &.with-index { width: calc(100% - 44px); }
}

.city-group {
  padding-bottom: 10px;
}

.city-group-title {
  margin: 0;
  padding: 8px 0;
  font-size: 13px;
  color: #1670c0;
}

.city-index {
  position: absolute;
  top: 0;
  right: -8px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  width: 44px;
  max-height: 100%;
  overflow: hidden;
  border-radius: 14px;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;

  &.dragging { background: #eef7fe; }

  button {
    flex: 1 1 0;
    min-height: 0;
    width: 100%;
    padding: 0;
    border: 0;
    background: transparent;
    color: #168de0;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    touch-action: none;
    -webkit-tap-highlight-color: transparent;
  }

  button.active,
  button:active {
    color: #fff;
    background: #168de0;
    border-radius: 12px;
  }

  button:focus-visible { outline: 2px solid #163b5c; outline-offset: -2px; }
}

.city-index-preview {
  position: absolute;
  z-index: 3;
  right: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  transform: translateY(-50%);
  border-radius: 14px;
  background: #168de0;
  color: #fff;
  font-size: 28px;
  font-weight: 600;
  box-shadow: 0 4px 14px rgba(22, 112, 192, 0.2);
  pointer-events: none;
}

.city-retry {
  display: block;
  margin: 10px auto 0;
  padding: 6px 12px;
  border: 0;
  border-radius: 8px;
  background: #eaf6fe;
  color: #168de0;
  font-size: 12px;
}

.city-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.city-item {
  box-sizing: border-box;
  width: calc((100% - 16px) / 3);
  min-height: 38px;
  padding: 7px 5px;
  border: 1px solid transparent;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.5;
  word-break: break-all;
  color: #5b7488;
  background: #eef4f9;
  cursor: pointer;

  &.selected {
    color: #fff;
    background: linear-gradient(100deg, #19a2e5, #146bd2);
  }
}

.month-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin: 4px 0 10px;
  font-size: 14px;
  color: #163b5c;

  .van-icon {
    color: #168de0;
    cursor: pointer;

    &.disabled {
      color: #c8d6e0;
      cursor: not-allowed;
    }
  }
}

.empty-tip {
  padding: 20px 0;
  font-size: 12px;
  color: #93a8b8;
  text-align: center;
}

.calendar {
  margin-top: 4px;
  border: 1px solid #eef4f9;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}

.calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background: #f4f9fd;
  border-bottom: 1px solid #eef4f9;

  span {
    padding: 8px 0;
    text-align: center;
    font-size: 12px;
    font-weight: 600;
    color: #7c8d9c;
  }
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
  background: #eef4f9;
}

.calendar-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50px;
  padding: 4px 0;
  background: #fff;
  cursor: pointer;
  transition: background 0.15s;

  b {
    font-size: 14px;
    font-weight: 600;
    color: #163b5c;
    line-height: 1.2;
  }

  small {
    margin-top: 2px;
    font-size: 10px;
    color: #7c8d9c;
    line-height: 1;
  }

  &.empty {
    background: #fafdff;
    cursor: default;
  }

  &.license {
    background: #f0f9ff;

    b { color: #0f6fb8; }
    small { color: #19a2e5; }
  }

  &.full,
  &.disabled {
    opacity: 0.55;
    cursor: not-allowed;

    small { color: #b5c3cd; }
  }

  &.selected {
    background: linear-gradient(100deg, #19a2e5, #146bd2);

    b, small { color: #fff; }
    opacity: 1;
  }
}

.time-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  font-size: 13px;
  color: #5b7488;
}

.time-label {
  margin-right: 4px;
  font-size: 12px;
  color: #7c8d9c;
}

.time-item {
  padding: 5px 12px;
  border-radius: 14px;
  font-size: 12px;
  color: #5b7488;
  background: #eef4f9;
  cursor: pointer;

  &.selected {
    color: #fff;
    background: linear-gradient(100deg, #19a2e5, #146bd2);
  }
}

.dialog-body {
  padding: 20px 24px 26px;
  font-size: 14px;
  line-height: 1.6;
  text-align: center;
  color: #4a6a82;
}

.submit-loading {
  position: absolute;
  top: 42%;
  left: 50%;
  color: #fff;
  transform: translate(-50%, -50%);
}
</style>
